import {getMappedProductAttributes} from '~/assets/js/utils/product_attributes_mapping'
import {formatDimensions} from '~/assets/js/utils/format_dimensions'
import useI18nExtended from '~/composables/usei18nExtended'

interface TableColumn {
  field: string
  title: string
  [key: string]: any
}

interface MappedAttribute {
  valueDouble?: number | null
  valueString?: string | null
  valueArrDouble?: number[]
  valueArrString?: string[]
  value?: any
  valueDec?: number | null
  unitOfMeasure?: string
  unit?: string
}

export default function useGrid() {
  const nuxtApp = useNuxtApp()
  const {tv} = useI18nExtended()

  const getColumnTitle = (tableColumns: TableColumn[], fieldName: string): string | undefined => {
    const column = tableColumns.find(c => c.field === fieldName)
    if (!column) {
      return
    }

    return column.title
  }

  const getNestedValue = <T = any>(fieldName: string, dataItem: Record<string, any>): T | undefined => {
    const path = fieldName.split('.')
    let data: any = dataItem
    path.forEach((p) => {
      data = data ? data[p] : undefined
    })

    return data as T | undefined
  }

  const getIndexPathValue = (
    indexPath: string,
    dataItem: Record<string, any>,
    isExcelImport: boolean = false,
    defaultValue: any = null
  ): any => {
    const locale = nuxtApp.$i18n?.locale?.value || 'en'
    const res = getMappedProductAttributes([{Attribute: {IndexPath: indexPath}}], dataItem) as MappedAttribute[]
    if (!isExcelImport && res && res.length === 1) {
      let item = res[0]
      let formattedValue = null
      // eslint-disable-next-line eqeqeq
      if (item?.valueDouble != null) {
        formattedValue = formatDimensions(item?.valueDouble, locale, item.unitOfMeasure === 'inch')
      // eslint-disable-next-line eqeqeq
      } else if (item?.valueString != null) {
        formattedValue = item?.valueString
      } else if (item?.valueArrDouble?.length && item.valueArrDouble.length > 0) {
        const stringValues = item?.valueArrDouble.map(v => formatDimensions(v, locale, item.unitOfMeasure === 'inch'))
        formattedValue = stringValues.join(', ')
      } else if (item?.valueArrString?.length && item.valueArrString.length > 0) {
        formattedValue = item?.valueArrString.join(', ')
      } else {
        formattedValue = item?.value ?? item?.valueDec
      }
      return formattedValue ?? defaultValue
    }

    if (res && res.length === 1) {
      let item = res[0]
      let formattedValue = item?.valueDec !== null ? formatDimensions(item?.valueDec, locale, item.unit === 'inch') : item?.value
      return formattedValue ?? defaultValue
    }
    return defaultValue
  }

  const getTranslatedIndexPathValue = (
    indexPath: string,
    dataItem: Record<string, any>
  ): string | number | null => {
    const res = getMappedProductAttributes([{Attribute: {IndexPath: indexPath}}], dataItem) as MappedAttribute[]
    if (res && res.length === 1) {
      let item = res[0]
      let shortName = indexPath.split('/')
      // eslint-disable-next-line eqeqeq
      if (item.valueDouble != null) {
        return item.valueDouble
      } else if (shortName.length === 2) {
        return tv(item.valueString as string, 'attributes.' + shortName[1] + '-values')
      } else {
        return item.valueString ?? null
      }
    }
    return null
  }

  const getTranslatedShortIndexPathValue = (
    indexPath: string,
    dataItem: Record<string, any>
  ): string | null => {
    const res = getMappedProductAttributes([{Attribute: {IndexPath: indexPath}}], dataItem) as MappedAttribute[]
    if (res && res.length === 1) {
      let item = res[0]
      let shortName = indexPath
      return tv(item.value.toString(), 'attributes.' + shortName + '-values')
    }
    return null
  }

  const getDate = (
    fieldName: string,
    dataItem: Record<string, any>,
    getNestedValueFn: typeof getNestedValue = getNestedValue
  ): Date | null => {
    const data = getNestedValueFn<any>(fieldName, dataItem)
    if (data) {
      if (Array.isArray(data)) {
        return data.length > 0 ? new Date(data[0].date) : null
      }
      return new Date(data)
    }
    return null
  }

  return {
    getColumnTitle,
    getNestedValue,
    getIndexPathValue,
    getTranslatedIndexPathValue,
    getTranslatedShortIndexPathValue,
    getDate
  }
}
